import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Info,
  Settings2,
  Pencil,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { formatDateTime } from '@/lib/utils/date';
import {
  useAgentGroupDetail,
  useAgentGroups,
  useDeleteAgentGroup,
} from '../api';
import { AGENT_TYPE_LABEL, type AgentGroupRow } from '../types';

export function GroupAgentListPage() {
  const navigate = useNavigate();
  const query = useAgentGroups();
  const del = useDeleteAgentGroup();
  const rows = query.data ?? [];

  const [pendingDelete, setPendingDelete] = useState<AgentGroupRow | null>(null);
  const [viewing, setViewing] = useState<AgentGroupRow | null>(null);

  // Load the agent list for the modal lazily.
  const detail = useAgentGroupDetail(viewing?.groupId ?? null);

  const columns = useMemo<ColumnDef<AgentGroupRow>[]>(
    () => [
      {
        accessorKey: 'groupId',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.groupId}
          </span>
        ),
      },
      {
        accessorKey: 'groupName',
        header: 'Group',
        cell: ({ row }) => (
          <Link
            to={`/update-group-agent/${row.original.groupId}`}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.groupName}
          </Link>
        ),
      },
      {
        id: 'reqDate',
        header: 'Created',
        size: 220,
        cell: ({ row }) =>
          formatDateTime(row.original.reqestDate ?? row.original.requestDate),
      },
      {
        id: 'agents',
        header: 'Call Patch Agent',
        size: 130,
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setViewing(row.original)}
          >
            <Users className="h-3.5 w-3.5" />
            View agents
          </Button>
        ),
      },
      {
        id: 'actions',
        header: 'Settings',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Row actions"
              >
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>{row.original.groupName}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() =>
                  navigate(`/update-group-agent/${row.original.groupId}`)
                }
              >
                <Pencil className="h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setPendingDelete(row.original)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [navigate],
  );

  const confirmDelete = () => {
    if (!pendingDelete) return;
    del.mutate(pendingDelete.groupId, {
      onSuccess: (res) => {
        toast.success(res?.message ?? `Deleted "${pendingDelete.groupName}"`);
        setPendingDelete(null);
      },
      onError: () => toast.error('Could not delete the agent group.'),
    });
  };

  return (
    <FillPage>
      <PageHeader
        title="Call Patch Agent"
        description="Pools of agent phone numbers used for call patching."
        actions={
          <Button asChild>
            <Link to="/create-group-agent">
              <Plus className="h-4 w-4" />
              Create group
            </Link>
          </Button>
        }
      />

      <DataTable<AgentGroupRow, unknown>
        tableId="agent-groups:list:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search groups…"
        emptyTitle="No agent groups"
        emptyDescription="Create a group of agents to receive patched inbound calls."
        getRowId={(row) => String(row.groupId)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="agent-groups.csv"
        pageSize={10}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Are you sure you want to delete?"
        description={
          <>
            Agent group{' '}
            <span className="font-medium text-foreground">{pendingDelete?.groupName}</span>{' '}
            will be permanently deleted. Campaigns using this group will no longer
            patch through.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={del.isPending}
        onConfirm={confirmDelete}
      />

      <Dialog open={viewing !== null} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              {viewing?.groupName}
            </DialogTitle>
            <DialogDescription>
              Agents that calls will hunt across when patching from this group.
            </DialogDescription>
          </DialogHeader>
          {detail.isLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Loading…
            </div>
          ) : !detail.data?.agents?.length ? (
            <div className="flex items-center justify-center gap-2 rounded-md border border-dashed border-border bg-muted/20 py-6 text-sm text-muted-foreground">
              <Info className="h-4 w-4" />
              No agents in this group.
            </div>
          ) : (
            <ol className="max-h-[50vh] space-y-2 overflow-y-auto">
              {detail.data.agents.map((a, i) => (
                <li
                  key={`${a.agentNumber}-${i}`}
                  className="flex items-center justify-between gap-3 rounded-md border border-border bg-card p-3"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">
                      {a.agentName}
                    </div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {a.agentNumber}
                    </div>
                  </div>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {AGENT_TYPE_LABEL[a.agentType]}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </DialogContent>
      </Dialog>
    </FillPage>
  );
}
